import {
  useOrgTransactionStatsByZipCodeQuery,
  usePerAgentOfferAcceptRateQuery,
  usePerAgentTransactionStatsQuery,
  usePerOfficeOfferAcceptRateQuery,
  usePerOfficeTransactionStatsQuery,
  useZipCodesGeoQuery,
} from '@client/graphql/__generated__/main-operations';
import {
  OrgTransactionStatsByZipCodeQueryVariables,
  ZipCodesGeoQueryVariables,
} from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseOrgPerformanceQueriesOptions {
  hasMapSuggestion: boolean;
  selectedZipCodes?: string[];
}

export const useOrgPerformanceQueries = ({
  hasMapSuggestion,
  selectedZipCodes,
}: UseOrgPerformanceQueriesOptions) => {
  const [zipCodesGeoFilters, setZipCodesGeoFilters] = useState<
    ZipCodesGeoQueryVariables['filters']
  >({
    zipCodes: [],
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionFilters = useMemo(
    () => ({
      intervalDays:
        Number(searchParams.get('intervalDays')) ||
        dayjs().diff(dayjs().startOf('year'), 'day'),
      priceMax: Number(searchParams.get('priceMax')) || 0,
      priceMin: Number(searchParams.get('priceMin')) || 0,
    }),
    [searchParams]
  );

  const setTransactionFilters = useCallback(
    (
      filters: Omit<
        OrgTransactionStatsByZipCodeQueryVariables['filters'],
        'zipCodes'
      >
    ) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (filters.intervalDays) {
          next.set('intervalDays', filters.intervalDays.toString());
        } else {
          next.delete('intervalDays');
        }

        if (filters.priceMax) {
          next.set('priceMax', filters.priceMax.toString());
        } else {
          next.delete('priceMax');
        }

        if (filters.priceMin) {
          next.set('priceMin', filters.priceMin.toString());
        } else {
          next.delete('priceMin');
        }

        return next;
      });
    },
    [setSearchParams]
  );

  const { data: zipCodesGeoData, loading: zipCodesGeoLoading } =
    useZipCodesGeoQuery({
      variables: {
        filters: zipCodesGeoFilters,
      },
    });

  const zipCodesFilter = hasMapSuggestion
    ? zipCodesGeoData?.zipCodesGeo?.map((zipCode) => zipCode.zip_code) ?? []
    : [];

  const {
    data: orgTransactionStatsByZipCodeData,
    loading: orgTransactionStatsByZipCodeLoading,
  } = useOrgTransactionStatsByZipCodeQuery({
    variables: {
      filters: {
        ...transactionFilters,
        zipCodes: zipCodesFilter,
      },
    },
    onCompleted: (data) => {
      if (!hasMapSuggestion) {
        setZipCodesGeoFilters({
          zipCodes: Array.from(
            new Set(
              data?.orgTransactionStatsByZipCode?.map(
                (zipCode) => zipCode.zip_code
              ) ?? []
            )
          ),
        });
      }
    },
  });

  // Reset zipCodesGeoFilters when hasMapSuggestion changes to false.
  // Needed if a zip code is not found by useZipCodesGeoQuery so the zipCodesFilter doesn't change.
  useEffect(() => {
    if (!hasMapSuggestion) {
      setZipCodesGeoFilters({
        zipCodes: Array.from(
          new Set(
            orgTransactionStatsByZipCodeData?.orgTransactionStatsByZipCode?.map(
              (zipCode) => zipCode.zip_code
            ) ?? []
          )
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMapSuggestion]);

  const {
    data: perAgentTransactionStatsData,
    loading: perAgentTransactionStatsLoading,
  } = usePerAgentTransactionStatsQuery({
    variables: {
      filters: {
        ...transactionFilters,
        zipCodes: zipCodesFilter,
      },
    },
  });

  const {
    data: perAgentOfferAcceptRateData,
    loading: perAgentOfferAcceptRateLoading,
  } = usePerAgentOfferAcceptRateQuery({
    variables: {
      filters: {
        ...transactionFilters,
        zipCodes: zipCodesFilter,
      },
    },
  });

  const {
    data: perAgentTransactionStatsDataBySelectedZipCodes,
    loading: perAgentTransactionStatsDataBySelectedZipCodesLoading,
  } = usePerAgentTransactionStatsQuery({
    variables: {
      filters: {
        ...transactionFilters,
        zipCodes: selectedZipCodes ?? [],
      },
    },
    skip: !selectedZipCodes?.length,
  });

  const {
    data: perAgentOfferAcceptRateDataBySelectedZipCodes,
    loading: perAgentOfferAcceptRateDataBySelectedZipCodesLoading,
  } = usePerAgentOfferAcceptRateQuery({
    variables: {
      filters: {
        ...transactionFilters,
        zipCodes: selectedZipCodes ?? [],
      },
    },
    skip: !selectedZipCodes?.length,
  });

  const { data: perOfficesTransactionStatsData } =
    usePerOfficeTransactionStatsQuery({
      variables: {
        filters: {
          ...transactionFilters,
          zipCodes: selectedZipCodes ?? [],
        },
      },
    });

  const { data: perOfficesOfferAcceptRateData } =
    usePerOfficeOfferAcceptRateQuery({
      variables: {
        filters: {
          ...transactionFilters,
          zipCodes: selectedZipCodes ?? [],
        },
      },
    });

  return {
    orgTransactionStatsByZipCodeData,
    perAgentTransactionStatsData,
    perAgentOfferAcceptRateData,
    zipCodesGeoData,
    transactionFilters,
    zipCodesGeoFilters,
    setZipCodesGeoFilters,
    setTransactionFilters,
    perAgentTransactionStatsDataBySelectedZipCodes,
    perAgentOfferAcceptRateDataBySelectedZipCodes,
    perOfficesTransactionStatsData,
    perOfficesOfferAcceptRateData,
    isLoading:
      orgTransactionStatsByZipCodeLoading ||
      perAgentTransactionStatsLoading ||
      perAgentOfferAcceptRateLoading ||
      zipCodesGeoLoading ||
      perAgentTransactionStatsDataBySelectedZipCodesLoading ||
      perAgentOfferAcceptRateDataBySelectedZipCodesLoading,
  };
};
